import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { readMeta } from '../../lib/page-prices'

// THE WASHES MENU. Robin, 6 Oct 2026: label "Washes", a dropdown of the washes
// with their live "from" prices, the add-ons, and "Compare all washes"
// (docs/COMPETITOR_REVIEW_THEVALETERS_2026-10-06.md §5).
//
// Nothing here is typed. The list is the gwmc:catalogue meta tag the server
// writes on every route (MetaTags::Helper#fold_catalogue): each wash with a
// page under /services carries its `path`, the one allowed label (DESIGN.md
// §3.2) and its price; `pages` are the add-ons that have a page. A wash
// retired in the database is not in the tag and leaves the menu by itself.
// app/views/gwmc/_header.html.haml paints the same list from the same hash.
//
// The links are plain <a href>, not NavLinks, on purpose: a wash page is
// server-rendered (pages/services/wash keeps the server's markup), so it is
// reached with a document load.
export const readWashesMenu = () => {
  const catalogue = readMeta('gwmc:catalogue', {}) || {}
  const washes = (Array.isArray(catalogue.washes) ? catalogue.washes : []).filter(wash => wash.path)
  const pages = (Array.isArray(catalogue.pages) ? catalogue.pages : []).filter(page => page.path)
  return { washes, pages }
}

export const WashesList = ({ menu }) => (
  <>
    <ul className="gwmc-washes-list">
      {menu.washes.map(wash => (
        <li key={wash.path}>
          <a className="gwmc-washes-item" href={wash.path}>
            <span className="gwmc-washes-name">
              {wash.name}
              {wash.label && <span className="gwmc-tag">{wash.label}</span>}
            </span>
            <span className="gwmc-washes-price">from {wash.price}</span>
          </a>
        </li>
      ))}
    </ul>
    {menu.pages.length > 0 && (
      <ul className="gwmc-washes-list gwmc-washes-list--addons">
        {menu.pages.map(page => (
          <li key={page.path}>
            <a className="gwmc-washes-item" href={page.path}>
              <span className="gwmc-washes-name">{page.name}</span>
              <span className="gwmc-washes-price">{page.price}</span>
            </a>
          </li>
        ))}
      </ul>
    )}
    <p className="gwmc-washes-more">
      <a className="gwmc-link" href="/services#extras">
        All add-ons
      </a>
      <a className="gwmc-link" href="/services">
        Compare all washes
      </a>
    </p>
  </>
)

// Desktop (960 and up; the nav it sits in is display:none below). Same
// disclosure behaviour as the phone menu: Escape, a click outside and a route
// change close it. No focus trap, no portal, no library.
//
// HOVER. Robin, 6 Oct 2026: "make sure washes drop down appears on
// mouseover". A mouse (pointerType 'mouse', so never a touch or a pen, whose
// synthetic enter would open it only for the tap to close it) opens it on
// entering the button or the panel, and leaving both closes it CLOSE_DELAY ms
// later, so the trip from the button down into the panel does not shut it.
// The panel is a child of the wrapper, so crossing between them is not a
// leave. A click on a menu the mouse has just opened keeps it open (it would
// otherwise open on the enter and shut on the click a moment later); after
// that a click toggles as before. Touch and keyboard are unchanged: a tap, or
// Enter / Space, toggles; Escape closes and puts focus back on the button.
const CLOSE_DELAY = 150

export const WashesMenu = React.memo(() => {
  const [menu] = useState(readWashesMenu)
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const buttonRef = useRef(null)
  const hoverOpened = useRef(false)
  const closeTimer = useRef(null)
  const location = useLocation()

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimer.current)
    closeTimer.current = null
  }, [])

  const close = useCallback(() => {
    cancelClose()
    hoverOpened.current = false
    setOpen(false)
  }, [cancelClose])

  const toggle = useCallback(
    event => {
      cancelClose()
      // event.detail is 0 for Enter / Space: the keyboard always toggles.
      if (hoverOpened.current && event.detail !== 0) {
        hoverOpened.current = false
        setOpen(true)
        return
      }
      hoverOpened.current = false
      setOpen(value => !value)
    },
    [cancelClose]
  )

  const onPointerEnter = useCallback(
    event => {
      if (event.pointerType !== 'mouse') return
      cancelClose()
      setOpen(value => {
        if (!value) hoverOpened.current = true
        return true
      })
    },
    [cancelClose]
  )

  const onPointerLeave = useCallback(
    event => {
      if (event.pointerType !== 'mouse') return
      cancelClose()
      closeTimer.current = setTimeout(close, CLOSE_DELAY)
    },
    [cancelClose, close]
  )

  useEffect(() => close, [close])
  useEffect(() => close(), [location.pathname, close])

  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = event => {
      if (event.key !== 'Escape') return
      const root = rootRef.current
      if (root && root.contains(document.activeElement) && buttonRef.current) buttonRef.current.focus()
      close()
    }
    const onPointerDown = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) close()
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open, close])

  if (menu.washes.length === 0) return null

  return (
    <div
      ref={rootRef}
      className="gwmc-header-washes"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}>
      <button
        ref={buttonRef}
        type="button"
        className="gwmc-header-link gwmc-header-washes-button"
        aria-expanded={open}
        aria-controls="gwmc-header-washes-panel"
        onClick={toggle}>
        Washes
        <svg className="gwmc-header-washes-chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <div id="gwmc-header-washes-panel" className="gwmc-header-washes-panel" hidden={!open}>
        <WashesList menu={menu} />
      </div>
    </div>
  )
})
